import React, { useState, useCallback, useEffect, useRef } from "react";
import { CheckOutlined } from "@ant-design/icons";
import { useMutation } from "@tanstack/react-query";
import { Button, Form, message, Typography } from "antd";
import { connect } from "dva";
import {} from "@/typedefs/user";
import { UNSUPPORTED_COUNTRIES_FOR_MOBILE_PHONE_NUMBER } from "@/utils/constants";
import { Validator } from "@/utils/validator";
import useCountDown from "@/hooks/useCountDown";
import InputOTP from "@/components/InputOTP";
import PhoneNumberInput, { flatPhoneNumber } from "@/components/PhoneNumberInput";
import sendPhoneNumberToken from "@/pages/Settings/components/SecuritySettingsView/queries/sendPhoneNumberToken";
import verifyPhoneNumberCode from "@/pages/Settings/components/SecuritySettingsView/queries/verifyPhoneCode";
import Box, { BoxHeader } from "@/pages/user/components/Box";
import { RESEND_COOLDOWN_SECONDS } from "@/pages/user/two-factor/constants";
import styles from "./PhoneVerificationForm.styles.less";
const { Text, Title } = Typography;
// Masks a phone number, showing only the last 4 digits
const maskPhoneNumber = (phone) => {
    const digits = phone.replace(/\D/g, "");
    if (digits.length < 4)
        return phone;
    const lastFour = digits.slice(-4);
    return `***-***-${lastFour}`;
};
const PhoneVerificationForm = ({ userId, phoneNumber: initialPhoneNumber, currentProfile, updatingProfile, showLogoutButton, showChangePhoneNumberButton, onComplete, onBack, dispatch, }) => {
    const hasSentInitialCode = useRef(false);
    const [newPhoneNumberForm] = Form.useForm();
    const newPhoneNumberFormRef = useRef({ ...newPhoneNumberForm, current: newPhoneNumberForm });
    const [step, setStep] = useState("verify");
    const [phoneNumber, setPhoneNumber] = useState(initialPhoneNumber);
    const [invalidVerificationCode, setInvalidVerificationCode] = useState(false);
    const [verificationCode, setVerificationCode] = useState("");
    // Format phone number for API (strip leading +)
    const formattedPhoneNumber = phoneNumber?.startsWith("+") ? phoneNumber.slice(1) : phoneNumber || "";
    const { isRunning: isResendCooldownRunning, start: startResendCooldown, secondsLeft, } = useCountDown({
        duration: RESEND_COOLDOWN_SECONDS,
        autoStart: true, // Start countdown immediately since code is sent on mount
    });
    const { mutate: mutateSendPhoneNumberToken, isPending: isSendingPhoneNumberToken } = useMutation({
        mutationFn: sendPhoneNumberToken,
        onSuccess: () => {
            message.success("Code sent successfully!");
            startResendCooldown();
        },
        onError: () => {
            message.error("Failed to send code. Please try again.");
        },
    });
    const { mutate: mutateVerifyCode, isPending: isVerifyingPhoneNumberCode } = useMutation({
        mutationFn: verifyPhoneNumberCode,
        onSuccess: () => {
            onComplete();
        },
        onError: () => {
            setInvalidVerificationCode(true);
        },
    });
    // Auto-send verification code on mount
    useEffect(() => {
        if (!hasSentInitialCode.current && userId && formattedPhoneNumber) {
            hasSentInitialCode.current = true;
            mutateSendPhoneNumberToken({ userId, phoneNumber: formattedPhoneNumber });
        }
    }, [userId, formattedPhoneNumber, mutateSendPhoneNumberToken]);
    const onResendVerificationCode = useCallback(() => {
        if (!userId || !formattedPhoneNumber) {
            return;
        }
        mutateSendPhoneNumberToken({ userId, phoneNumber: formattedPhoneNumber });
    }, [userId, formattedPhoneNumber, mutateSendPhoneNumberToken]);
    const onVerifyVerificationCode = useCallback((code) => {
        if (!userId) {
            message.error("Unable to verify code. Please refresh the page and try again.");
            return;
        }
        mutateVerifyCode({ userId, token: code.replace(/\s/g, "") });
    }, [userId, mutateVerifyCode]);
    const handleCodeChange = useCallback((value) => {
        setVerificationCode(value);
        setInvalidVerificationCode(false);
    }, []);
    const onChangePhoneNumber = useCallback((values) => {
        if (!userId)
            return;
        const string = flatPhoneNumber(values.newPhoneNumber);
        const updateProfilePayload = {
            ...currentProfile,
            phoneNumber: string,
        };
        const promise = dispatch({
            type: "user/updateDataProfile",
            payload: updateProfilePayload,
        });
        promise
            .then(() => {
            setPhoneNumber(string);
            mutateSendPhoneNumberToken({ userId, phoneNumber: string });
            setStep("verify");
        })
            .catch(() => {
            message.error("Failed to update phone number. Please try again.");
        });
    }, [currentProfile, userId, mutateSendPhoneNumberToken, dispatch]);
    // Check if verification code is valid (6 digits)
    const isValidCode = verificationCode.replace(/\s/g, "").length === 6;
    if (step === "change-phone-number") {
        return (React.createElement(Box, { width: "30%" },
            React.createElement(BoxHeader, { showLogoutButton: showLogoutButton },
                React.createElement("img", { alt: "logo", className: styles.logo, src: "/img/autosled-logo.png" }),
                React.createElement(Title, { level: 3, style: { marginBottom: 8, marginTop: 16 } }, "Enter Your Phone Number"),
                React.createElement(Text, { type: "secondary", style: { textAlign: "center" } }, "We'll send you a verification code")),
            React.createElement("div", { className: styles.formContainer },
                React.createElement("div", { style: {
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        width: "100%",
                    } },
                    React.createElement(Form, { form: newPhoneNumberForm, layout: "vertical", onFinish: onChangePhoneNumber, style: {
                            width: "100%",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                        } },
                        React.createElement("div", { style: { width: "328px" } },
                            React.createElement(PhoneNumberInput, { showExtensionInput: false, excludeCountries: UNSUPPORTED_COUNTRIES_FOR_MOBILE_PHONE_NUMBER, borderRadius: 8, formItemProps: {
                                    name: "newPhoneNumber",
                                    label: "Phone Number",
                                    formRef: newPhoneNumberFormRef,
                                    rules: [Validator.required("Phone number is required")],
                                } })),
                        React.createElement(Button, { type: "primary", size: "large", block: true, htmlType: "submit", loading: updatingProfile, className: styles.submitButton }, "Send Verification Code"),
                        React.createElement(Button, { type: "link", onClick: () => setStep("verify"), className: styles.backButton }, "Back"))))));
    }
    return (React.createElement(Box, { width: "30%" },
        React.createElement(BoxHeader, { showLogoutButton: showLogoutButton },
            React.createElement("img", { alt: "logo", className: styles.logo, src: "/img/autosled-logo.png" }),
            React.createElement(Title, { level: 3, style: { marginBottom: 8, marginTop: 16 } }, "Verify Your Phone Number"),
            React.createElement(Text, { type: "secondary", style: { textAlign: "center" } }, `Enter the code sent to ${maskPhoneNumber(phoneNumber || "")}`)),
        React.createElement("div", { className: styles.formContainer },
            React.createElement("div", { style: { display: "flex", flexDirection: "column", alignItems: "center" } },
                React.createElement(Text, { style: { marginBottom: 16 } }, "Verification Code"),
                React.createElement(InputOTP, { length: 6, value: verificationCode, onChange: handleCodeChange, onComplete: onVerifyVerificationCode, disabled: isVerifyingPhoneNumberCode, error: invalidVerificationCode, autoFocus: true }),
                invalidVerificationCode && (React.createElement("div", { className: "ant-form-item-explain-error", style: { marginTop: 8, marginBottom: 8 } }, "Invalid verification code. Please try again.")),
                React.createElement(Button, { type: "primary", size: "large", block: true, disabled: !isValidCode, loading: isVerifyingPhoneNumberCode, icon: React.createElement(CheckOutlined, null), onClick: () => onVerifyVerificationCode(verificationCode), style: isValidCode ? { backgroundColor: "#52C41A", marginTop: 24 } : { marginTop: 24 }, className: styles.submitButton }, "Verify & Continue"),
                React.createElement("div", { className: styles.linkRow },
                    showChangePhoneNumberButton && (React.createElement(Button, { type: "link", onClick: () => setStep("change-phone-number"), className: styles.changeNumberButton }, "Change Phone Number")),
                    React.createElement(Button, { type: "link", onClick: onResendVerificationCode, disabled: isVerifyingPhoneNumberCode || isResendCooldownRunning, loading: isSendingPhoneNumberToken, className: styles.resendButton }, isResendCooldownRunning ? `Resend Code (${secondsLeft}s)` : "Resend Code")),
                React.createElement(Button, { type: "link", onClick: onBack, className: styles.backButton }, "Back")))));
};
export default connect(({ user, loading, }) => ({
    currentProfile: user.currentProfile,
    updatingProfile: loading.effects["user/updateDataProfile"],
}))(PhoneVerificationForm);
